import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ArrowUp, Paperclip, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { canAccessDigitalTwinDemo } from '../config/digitalTwinDemoAccess';
import {
  recommendedFor,
  agentsByCategory,
  jobTitleLabel,
  AGENTS
} from '../data/agentCatalog';

/**
 * Role-driven home with a primary "Recommended for you" row (6 cards based on
 * the user's job title), then a browse-by-category section listing the
 * remaining agents grouped per CATEGORY_ORDER.
 *
 * No marketplace search/filter UI — recommendations are implicit, the rest is
 * a quick reference. Each card navigates to its agent (most agents go to
 * /jobotics-agent?agentId=<slug>; contract review keeps its own page).
 */
export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const role = user?.jobTitle;
  // Per-agent ACL passed into the catalog helpers so gated agents (e.g.
  // Project Heatmap Agent — internal id still `digital_twin`) get filtered
  // for users not on the allowlist.
  const acl = useMemo(
    () => ({ canAccessDigitalTwin: canAccessDigitalTwinDemo(user?.email) }),
    [user?.email]
  );
  const recommended = useMemo(() => recommendedFor(role, 6, acl), [role, acl]);
  const recommendedIds = useMemo(
    () => recommended.map((a) => a.id),
    [recommended]
  );

  // Demo section: the agents we want featured at the top of the page for
  // the 3 demo accounts (robert / jake / skye per digitalTwinDemoAccess.js).
  // For everyone else the section is hidden; the same agents (except
  // digital_twin which is gated) remain available in their normal categories.
  const DEMO_SECTION_IDS = ['digital_twin', 'material_matrix_mep', 'contract_review', 'large_file', 'rsi_extractor', 'drawing_markup', 'sld_review', 'scheduling_agent'];
  const demoAgents = useMemo(() => {
    if (!acl.canAccessDigitalTwin) return [];
    const byId = Object.fromEntries(AGENTS.map((a) => [a.id, a]));
    return DEMO_SECTION_IDS.map((id) => byId[id]).filter(Boolean);
  }, [acl.canAccessDigitalTwin]);
  const demoIds = useMemo(() => demoAgents.map((a) => a.id), [demoAgents]);

  // Custom user-created agents (live in Postgres). Fetched once at
  // mount so the Home category browser can render them alongside the
  // built-in catalog under their declared category.
  const [customAgents, setCustomAgents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/rsi-agents', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data?.agents) return;
        setCustomAgents(data.agents.map((a) => ({
          id: a.id,
          name: a.name,
          emoji: '\u{1F9E0}',
          category: a.category,
          route: `/jobotics-agent?agentId=${encodeURIComponent(a.id)}`,
          blurb: a.description || `Custom ${a.task_type} agent.`,
          isUserAgent: true,
          createdBy: a.created_by,
          mine: !!a.mine,
        })));
      })
      .catch(() => { /* unauth'd or DB down — built-ins still render */ });
    return () => { cancelled = true; };
  }, []);

  // Browse list = everything NOT already in the personalized "Recommended for
  // you" row OR the Demo section above. Also skip the "Recommended" category
  // (those agents live in the personalized row already). Custom user-created
  // agents are folded into their declared category so a "Construction" agent
  // built in the UI shows up next to the built-in Construction agents.
  const rest = useMemo(
    () => {
      const skip = new Set([...recommendedIds, ...demoIds]);
      const builtInGroups = agentsByCategory([...skip], acl)
        .filter((g) => g.category !== 'Recommended');
      const merged = builtInGroups.map((g) => ({ ...g, agents: g.agents.slice() }));
      // Inject each custom agent into its declared category. If the
      // category bucket doesn't exist yet (e.g. user picked a category
      // no built-in agent uses), append a new one.
      for (const ca of customAgents) {
        if (skip.has(ca.id)) continue;
        const bucket = merged.find((g) => g.category === ca.category);
        if (bucket) {
          bucket.agents.push(ca);
        } else {
          merged.push({ category: ca.category, agents: [ca] });
        }
      }
      return merged.filter((g) => g.agents.length > 0);
    },
    [recommendedIds, demoIds, acl, customAgents]
  );

  // Strip Project Heatmap from the "Recommended for you" row too, so it
  // appears exclusively in the Demo section (not in two places).
  const recommendedFiltered = useMemo(
    () => recommended.filter((a) => !demoIds.includes(a.id)),
    [recommended, demoIds]
  );

  const hasRole = Boolean(role);
  const roleLabel = jobTitleLabel(role) || 'your role';
  const displayName = (user?.displayName || user?.name || '').split(' ')[0];

  return (
    <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex gap-10 items-start">
        <main className="flex-1 min-w-0 space-y-8 sm:space-y-12">
      {/* Hero — greeting + AI composer + quick prompts, mirroring the reference
          home: a big "Hi, <name>." over an "ask anything" composer above the
          agent library. The composer routes the prompt into the Jobotics Agent. */}
      <section className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl text-ink font-normal tracking-tight">
              <span className="text-sylvan-500">Hi{displayName ? ',' : ''}</span> {displayName || 'there'}.
            </h1>
            <p className="text-ink mt-2 sm:mt-3 text-base sm:text-lg">What would you like to accomplish today?</p>
            <p className="text-ink-muted mt-1 text-sm">
              {hasRole ? (
                <>Tuned for your work as <strong className="font-medium text-ink-body">{roleLabel}</strong>.</>
              ) : (
                <>Ask anything, or pick an agent below. <button type="button" onClick={() => navigate('/onboarding')} className="text-sylvan-500 hover:text-sylvan-600 underline underline-offset-2">Tell us your role</button> to personalize.</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-1">
            <button
              type="button"
              onClick={() => navigate('/rsi-agents')}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-ink-body hover:text-ink hover:bg-canvas-strong transition-colors"
            >
              My agents
            </button>
            <button
              type="button"
              onClick={() => navigate('/rsi-agents/new')}
              className="rounded-md border border-hairline-strong bg-white hover:bg-canvas-soft text-ink px-4 py-1.5 text-sm font-medium transition-colors"
            >
              + Build your own
            </button>
          </div>
        </div>

        <HeroComposer
          onPrompt={(q, agentId) =>
            navigate(`/jobotics-agent?agentId=${encodeURIComponent(agentId)}`, { state: { initialQuestion: q } })
          }
        />
      </section>

      {/* Demo + Recommended are now foldable like Browse-by-category —
          expanded by default but a user can collapse them if they want
          to focus on a specific section. Keeps the page consistent: every
          section behaves the same. */}
      {demoAgents.length > 0 && (
        <FoldableSection
          title="Demo"
          badge={{ label: 'Allowlisted accounts', tone: 'teal' }}
          count={demoAgents.length}
          defaultOpen={true}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {demoAgents.map((a) => (
              <AgentCard key={a.id} agent={a} onOpenInternal={(p) => navigate(p)} />
            ))}
          </div>
        </FoldableSection>
      )}

      <FoldableSection
        title="Recommended for you"
        count={recommendedFiltered.length}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {recommendedFiltered.map((a) => (
            <AgentCard key={a.id} agent={a} onOpenInternal={(p) => navigate(p)} />
          ))}
        </div>
      </FoldableSection>

      {/* Browse by category — collapsed by default so the page leads with
          Demo + Recommended. Each section is a click-to-expand pill that
          shows the agent count so users still see the breadth without
          having every card painted. */}
      {rest.length > 0 && (
        <section>
          <h2 className="text-base font-medium text-ink mb-3">Browse by category</h2>
          <div className="space-y-2">
            {rest.map(({ category, agents }) => (
              <CategorySection
                key={category}
                category={category}
                agents={agents}
                onOpenInternal={(p) => navigate(p)}
              />
            ))}
          </div>
        </section>
      )}
        </main>

        {/* Right rail — Industry insights + Support. Mirrors the reference
            home's right panel; hidden below xl so the main column keeps full
            width on narrower screens. */}
        <RightInsightPanel />
      </div>
    </div>
  );
}

/**
 * Generic foldable wrapper used by the Demo + Recommended sections.
 * Header is a chevron + title + optional badge + count, click toggles
 * the body. defaultOpen=true gives the visually-uncollapsed look on
 * first load while still letting users collapse to focus elsewhere.
 */
function FoldableSection({ title, badge, count, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const BADGE_TONE = {
    teal:   'text-teal-700 bg-teal-50 border-teal-200',
    slate:  'text-slate-600 bg-slate-100 border-slate-200',
  };
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 mb-3 group text-left"
      >
        {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        <h2 className="text-lg font-semibold text-slate-800 group-hover:text-slate-900">{title}</h2>
        {badge && (
          <span className={`text-xs uppercase tracking-wide border rounded-full px-2 py-0.5 ${BADGE_TONE[badge.tone] || BADGE_TONE.slate}`}>
            {badge.label}
          </span>
        )}
        {typeof count === 'number' && (
          <span className="text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
            {count}
          </span>
        )}
        <span className="ml-auto text-xs text-slate-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div>{children}</div>}
    </section>
  );
}

/**
 * Collapsible "Browse by category" group. Default-collapsed to keep the page
 * scannable — the Demo + Recommended sections at the top do the heavy lifting
 * for first-time users, and the long tail of category-specific agents stays
 * one click away without overwhelming.
 */
function CategorySection({ category, agents, onOpenInternal }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
          <span className="font-semibold text-slate-800">{category}</span>
          <span className="text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
            {agents.length}
          </span>
        </div>
        <span className="text-xs text-slate-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((a) => (
              <AgentCard key={a.id} agent={a} muted onOpenInternal={onOpenInternal} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Hero AI composer — the prototype's "ask anything" box. A hairline card on
   cream with a textarea, a light tool row, and an orange send affordance,
   plus a strip of quick-prompt chips. Submitting routes the prompt into the
   Jobotics Agent (via router state read by JoboticsAgent on mount). */
// Each quick prompt is routed to the agent that best handles it. The
// free-text composer (no chip) lands on the general Deep Search agent.
const QUICK_PROMPTS = [
  { label: 'Review a contract',         agentId: 'contract_review' },
  { label: 'Analyze a drawing set',     agentId: 'large_file' },
  { label: 'Generate a material matrix', agentId: 'material_matrix_mep' },
  { label: 'Open project heatmap',      agentId: 'digital_twin' },
  { label: 'Create drawing markups',    agentId: 'drawing_markup' },
];
const DEFAULT_AGENT = 'deep_search';

function HeroComposer({ onPrompt }) {
  const [text, setText] = useState('');
  const taRef = useRef(null);
  const submit = () => {
    const q = text.trim();
    if (q) onPrompt(q, DEFAULT_AGENT);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-hairline-strong bg-white focus-within:border-sylvan-500 transition-colors">
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder="Ask anything — or describe what you need from your drawings, specs, or project data…"
          className="w-full resize-none bg-transparent px-4 pt-4 pb-2 text-ink placeholder:text-ink-muted outline-none text-[15px] leading-relaxed"
        />
        <div className="flex items-center justify-between px-3 pb-3">
          <div className="flex items-center gap-1 text-ink-muted">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-canvas-strong transition-colors cursor-default" title="Attach (open an agent to upload)">
              <Paperclip className="w-4 h-4" />
            </span>
            <span className="inline-flex items-center gap-1 px-2 h-8 rounded-md text-xs text-ink-muted">
              <Sparkles className="w-3.5 h-3.5 text-sylvan-500" /> Jobotics Agent
            </span>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!text.trim()}
            aria-label="Send"
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-sylvan-500 text-white hover:bg-sylvan-600 disabled:bg-canvas-strong disabled:text-ink-muted transition-colors"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {QUICK_PROMPTS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => onPrompt(p.label, p.agentId)}
            className="text-sm text-ink-body bg-white border border-hairline rounded-full px-3.5 py-1.5 hover:border-sylvan-300 hover:text-ink transition-colors"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* Right rail — "Industry insights" (live AEC market framing) + a Support
   card, mirroring the reference home's right panel. Figures are the demo
   dataset that backs the AEC Research agent. Hidden below xl. */
const INSIGHT_STATS = [
  { label: 'MEP Labor',  value: '$127', unit: '/hr',    trend: '+8% QoQ' },
  { label: 'Copper Pipe', value: '+11%', unit: 'YoY',   trend: 'Tariff driven' },
  { label: 'MEP Awards', value: '$4.2B', unit: 'active', trend: '+214 proj' },
];
const INSIGHT_FEED = [
  { tag: 'Labor',     title: 'MEP Labor · Bay Area',      desc: 'Journeyman electrician rate at $127/hr — up 8% from Q4 2025, the tightest labor market since 2019.' },
  { tag: 'Materials', title: 'Copper Pipe · 4" Installed', desc: 'Bay Area at $48/LF — 6% above national average, tariff pressure continuing into Q3.' },
  { tag: 'Market',    title: 'MEP Awards · West Region',   desc: '$4.2B across 214 projects over $5M. Data centers + healthcare driving 62% of new awards in 2026.' },
];

function RightInsightPanel() {
  const navigate = useNavigate();
  // Live AEC market data via /api/aec-insights (AEC MCP, cached server-side).
  // Falls back to the static constants while loading or if the fetch fails,
  // so the panel always renders.
  const [stats, setStats] = useState(INSIGHT_STATS);
  const [feed, setFeed] = useState(INSIGHT_FEED);
  const [live, setLive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const apply = (d) => {
      if (cancelled || !d) return false;
      if (Array.isArray(d.stats) && d.stats.length) setStats(d.stats);
      if (Array.isArray(d.feed) && d.feed.length) setFeed(d.feed);
      const isLive = d.source === 'live';
      setLive(isLive);
      return isLive;
    };
    const load = (refresh) => fetch(`/api/aec-insights${refresh ? '?refresh=1' : ''}`, { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then(apply)
      .catch(() => false);
    // On login the home page renders → the FIRST fetch sends ?refresh=1 so the
    // server nudges a recompute if its cache is stale (rate-limited to 30 min
    // server-side, so frequent logins don't re-run the expensive pull). Then
    // poll plainly (every 10s, up to ~2 min) until live data arrives — cold
    // start can take 20-40s. Self-heals on the same visit, no reload needed.
    let attempts = 0;
    const MAX_ATTEMPTS = 12;
    const poll = (first) => {
      if (cancelled) return;
      load(first).then((isLive) => {
        if (cancelled || isLive) return;
        attempts += 1;
        if (attempts < MAX_ATTEMPTS) timers.push(setTimeout(() => poll(false), 10000));
      });
    };
    poll(true);
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, []);
  return (
    <aside className="w-80 shrink-0 hidden xl:block space-y-4">
      <div className="rounded-lg border border-hairline bg-white overflow-hidden">
        <div className="flex items-baseline justify-between px-5 pt-5 pb-3">
          <span className="text-sm font-medium text-ink">Industry insights</span>
          <span className="text-[11px] uppercase tracking-wider text-ink-muted">{live ? 'AEC · Live' : 'AEC · Weekly'}</span>
        </div>
        <div className="grid grid-cols-3 gap-px bg-hairline border-y border-hairline">
          {stats.map((s) => (
            <div key={s.label} className="bg-white px-3 py-3">
              <div className="text-[11px] text-ink-muted truncate">{s.label}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-lg text-ink font-mono">{s.value}</span>
                <span className="text-[11px] text-ink-muted">{s.unit}</span>
              </div>
              <div className="text-[11px] text-emerald-700 mt-0.5 truncate">{s.trend}</div>
            </div>
          ))}
        </div>
        <div className="divide-y divide-hairline-soft">
          {feed.map((f) => (
            <div key={f.title} className="px-5 py-3.5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-ink-body bg-canvas-strong rounded px-1.5 py-0.5">{f.tag}</span>
                <span className="text-[10px] text-ink-muted">Updated today</span>
              </div>
              <div className="text-sm text-ink font-medium">{f.title}</div>
              <div className="text-[12px] text-ink-body leading-relaxed mt-0.5">{f.desc}</div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => navigate('/jobotics-agent?agentId=aec_research')}
          className="w-full text-left px-5 py-3 text-sm text-sylvan-500 hover:text-sylvan-600 hover:bg-canvas-soft border-t border-hairline transition-colors"
        >
          Open AEC Research →
        </button>
      </div>

      <div className="rounded-lg border border-hairline bg-ink text-canvas p-5">
        <div className="text-sm font-medium">Support Center</div>
        <div className="text-[13px] text-canvas/70 mt-1 leading-relaxed">Get expert guidance from our team.</div>
        <a
          href="mailto:robert.dong@jobotics.ai"
          className="inline-flex items-center gap-1 mt-3 text-[13px] font-medium text-canvas border border-canvas/25 rounded-md px-3 py-1.5 hover:bg-white/10 transition-colors"
        >
          Contact us ↗
        </a>
      </div>
    </aside>
  );
}

function AgentCard({ agent, onOpenInternal, muted = false }) {
  const disabled = !agent.route;
  const isExternal = !!agent.external;

  // External-link agents (e.g. Project Twin Agent → demo.jobotics.ai) render
  // as an <a target="_blank"> so the user gets a new tab and we don't muddle
  // SPA history with off-site navigation.
  const Comp = isExternal ? 'a' : 'button';
  const extraProps = isExternal
    ? { href: agent.route, target: '_blank', rel: 'noopener noreferrer' }
    : { type: 'button', onClick: () => agent.route && onOpenInternal(agent.route), disabled };

  return (
    <Comp
      {...extraProps}
      className={`text-left rounded-lg border bg-white p-5 transition block
        ${disabled ? 'opacity-60 cursor-not-allowed border-hairline' : 'border-hairline hover:border-sylvan-300 hover:bg-canvas-soft cursor-pointer'}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl leading-none" aria-hidden>{agent.emoji}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-ink truncate">{agent.name}</h3>
            {disabled && (
              <span className="text-xs text-ink-muted bg-canvas-strong rounded-full px-2 py-0.5">
                Coming soon
              </span>
            )}
          </div>
          <p className="text-sm text-ink-body mt-1 line-clamp-3 leading-relaxed">{agent.blurb}</p>
        </div>
      </div>
      {/* Data-source tag pills (DIGITAL TWIN / VISTA / UPLOAD / KB) removed per
          spec — they cluttered every card and weren't actionable for the user. */}
    </Comp>
  );
}
